import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  Avatar,
  Box,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { deleteItem } from '../../services/items';
import { fetchStoreItems } from '../../services/store';
import type { ItemDTO } from '../../dto/item/ItemDTO';
import ProductForm from './ProductForm';
import { showError, showSuccess } from '../../components/Alert';
import '../../styles/ProductsTab.css';

interface ProductsTabProps {
  storeId: string;
}

export default function ProductsTab({ storeId }: ProductsTabProps) {
  const [items, setItems] = useState<ItemDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [editItem, setEditItem] = useState<ItemDTO | null>(null);

  // Delete confirmation state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ItemDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Regular load (used when opening/closing the form, or initial mount)
  const load = () => {
    if (!storeId) return;
    setLoading(true);

    fetchStoreItems({ storeId })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data?.items) ? data.items : [];

        if (!Array.isArray(data?.items) && typeof data !== 'undefined') {
          console.error(
            '[ProductsTab] Unexpected payload from fetchStoreItems:',
            data
          );
          showError('Failed to load store products.');
        }

        setItems(list);
      })
      .catch((err) => {
        console.error('[ProductsTab] Failed to load items:', err);
        const message =
          typeof err?.response?.data === 'string'
            ? err.response.data
            : err?.message ?? 'Failed to load store products.';
        showError(message);
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [openForm, storeId]);

  const requestDelete = (item: ItemDTO) => {
    setItemToDelete(item);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteItem(itemToDelete.id);

      // Optimistic remove from UI immediately
      setItems((prev) =>
        prev ? prev.filter((i) => i.id !== itemToDelete.id) : prev
      );

      showSuccess(`"${itemToDelete.name}" was deleted.`);
      setConfirmOpen(false);
      setItemToDelete(null);

      // Force a fresh list to avoid any caching / eventual consistency issues
      load();
    } catch (err: any) {
      const msg =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.message ?? 'Failed to delete the item.';
      showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  if (loading && items.length === 0) return <CircularProgress />;

  return (
    <>
      {/* Right-aligned add button */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={<Add />}
          onClick={() => {
            setEditItem(null);
            setOpenForm(true);
          }}
          variant="outlined"
          sx={{
            color: '#424242',
            borderColor: '#e0e0e0',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bdbdbd' },
          }}
        >
          Add Product
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Thumbnail</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Sale (%)</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Available</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                >
                  No products found for this store yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Avatar
                    variant="square"
                    src={item.thumbnail}
                    alt={item.name}
                    className="product-avatar"
                  />
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>${item.price}</TableCell>
                <TableCell>{item.sale}%</TableCell>
                <TableCell>{item.currency}</TableCell>
                <TableCell>{item.isAvailable ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item.tags.join(', ')}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditItem(item);
                        setOpenForm(true);
                      }}
                      aria-label="edit product"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => requestDelete(item)}
                      aria-label="delete product"
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Create/Edit dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editItem ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent>
          <ProductForm
            product={editItem}
            onSuccess={() => setOpenForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => (!deleting ? setConfirmOpen(false) : undefined)}
      >
        <DialogTitle>Delete product?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will permanently delete <strong>{itemToDelete?.name}</strong>.
            This action cannot be undone. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
